import type {
  MagicSlidePayload,
  MasterLayoutId,
  Slide,
  SlideElement,
  SmartBlockKind,
} from '../types/slide';
import { ASSETS, BRAND, CANVAS, COMPANY_LEGAL_NAME, CONFIDENTIALITY } from './brand';
import { uid } from './id';

export const LAYOUT_META: {
  id: MasterLayoutId;
  name: string;
  description: string;
}[] = [
  { id: 'title_slide', name: 'Cover', description: 'Photo background, title and subtitle' },
  { id: 'exec_summary', name: 'Summary', description: 'Header and bullets' },
  { id: 'kpi_3_card', name: 'KPI cards', description: 'Three metric cards' },
  { id: 'two_column_split', name: 'Two column', description: 'Left / right content' },
  { id: 'three_column', name: 'Three column', description: 'Three equal content columns' },
  { id: 'chart_and_takeaways', name: 'Chart', description: 'Chart with takeaways' },
  { id: 'timeline_roadmap', name: 'Timeline', description: 'Milestone roadmap' },
  { id: 'agenda', name: 'Agenda', description: 'Numbered discussion topics' },
  { id: 'section_divider', name: 'Section', description: 'Mid-deck section break' },
  { id: 'quote_highlight', name: 'Quote', description: 'Large quote and attribution' },
  { id: 'image_text', name: 'Image + text', description: 'Photo with supporting bullets' },
  { id: 'blank_custom', name: 'Custom', description: 'Blank canvas with brand frame — build your own' },
  { id: 'closing', name: 'Thank you', description: 'Closing slide' },
];

const FAVORITES_KEY = 'slidesprint.favoriteLayouts';

/** User-pinned layouts for quick access (persisted in localStorage). */
export function getFavoriteLayouts(): MasterLayoutId[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const allowed = new Set(LAYOUT_META.map((l) => l.id));
    return parsed.filter((id): id is MasterLayoutId => typeof id === 'string' && allowed.has(id as MasterLayoutId));
  } catch {
    return [];
  }
}

export function toggleFavoriteLayout(id: MasterLayoutId): MasterLayoutId[] {
  const current = getFavoriteLayouts();
  const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
  return next;
}

/**
 * Geometry from the AST template's "Normal" layout, in 1280×720 canvas px:
 * header 52,26 1075×48 · body 52,87 1075×566 · right image strip at x 1175 ·
 * footer bar from y 673.
 */
const CONTENT = { x: 52, w: 1075, top: 100, bottom: 650 } as const;
const STRIP_X = 1175;
const BAR_Y = 673;

/** Locked AST content-slide frame: right image strip and blue footer bar. */
function contentChrome(slideIndex: number): SlideElement[] {
  return [
    {
      id: uid('sky_'),
      type: 'shape',
      name: 'Side panel',
      x: STRIP_X,
      y: 0,
      w: CANVAS.width - STRIP_X,
      h: 176,
      fill: BRAND.sky,
      locked: true,
      zIndex: 90,
    },
    {
      id: uid('strip_'),
      type: 'image',
      name: 'Side image',
      x: STRIP_X,
      y: 176,
      w: CANVAS.width - STRIP_X,
      h: BAR_Y - 176,
      src: ASSETS.sideStrip,
      fit: 'cover',
      radius: 0,
      locked: true,
      zIndex: 90,
    },
    {
      id: uid('bar_'),
      type: 'shape',
      name: 'Footer bar',
      x: 0,
      y: BAR_Y,
      w: CANVAS.width,
      h: CANVAS.height - BAR_Y,
      fill: BRAND.blue,
      locked: true,
      zIndex: 90,
    },
    {
      id: uid('foot_'),
      type: 'footer',
      x: CONTENT.x,
      y: BAR_Y + 14,
      w: 420,
      h: 20,
      locked: true,
      zIndex: 100,
      text: CONFIDENTIALITY,
      color: BRAND.white,
      font: 'label',
      fontSize: 13,
      fontWeight: 500,
    },
    {
      id: uid('co_'),
      type: 'logo',
      name: 'Company name',
      x: 680,
      y: BAR_Y + 14,
      w: 500,
      h: 20,
      locked: true,
      zIndex: 100,
      text: COMPANY_LEGAL_NAME,
      color: BRAND.white,
      font: 'label',
      fontSize: 14,
      fontWeight: 500,
      align: 'right',
    },
    {
      id: uid('num_'),
      type: 'slide_number',
      x: 1196,
      y: BAR_Y + 14,
      w: 52,
      h: 20,
      locked: true,
      zIndex: 100,
      text: String(slideIndex + 1),
      color: BRAND.white,
      font: 'label',
      fontSize: 14,
      fontWeight: 600,
      align: 'right',
    },
  ];
}

/** White AST logo used on the cover and closing slides. */
function coverLogo(): SlideElement {
  return {
    id: uid('logo_'),
    type: 'logo',
    x: 47,
    y: 47,
    w: 192,
    h: 69,
    src: ASSETS.logoWhite,
    fit: 'contain',
    locked: true,
    zIndex: 100,
  };
}

function textEl(
  partial: Omit<SlideElement, 'id' | 'type'> & { text: string },
): SlideElement {
  return {
    id: uid(),
    type: 'text',
    fontSize: 25,
    fontWeight: 400,
    color: BRAND.ink,
    font: 'body',
    align: 'left',
    ...partial,
  };
}

/** Slide header in the template's style: 20pt bold bright blue. */
function header(text: string): SlideElement {
  return textEl({
    x: CONTENT.x,
    y: 26,
    w: CONTENT.w,
    h: 48,
    text,
    fontSize: 36,
    fontWeight: 700,
    color: BRAND.bright,
  });
}

function bulletText(items: string[]): string {
  return items.map((b) => `• ${b}`).join('\n\n');
}

function contentSlide(layoutId: MasterLayoutId, slideIndex: number, elements: SlideElement[]): Slide {
  return {
    id: uid('slide_'),
    layoutId,
    background: BRAND.white,
    elements: [...contentChrome(slideIndex), ...elements],
  };
}

export function buildLayout(
  layoutId: MasterLayoutId,
  slideIndex = 0,
  content?: Partial<MagicSlidePayload>,
): Slide {
  const c = content ?? {};
  const title = c.title || defaultTitle(layoutId);
  const subtitle = c.subtitle || '';
  const bullets = c.bullets?.length
    ? c.bullets
    : ['Key point one', 'Key point two', 'Key point three'];

  switch (layoutId) {
    case 'title_slide':
      return {
        id: uid('slide_'),
        layoutId,
        background: BRAND.blue,
        backgroundImage: ASSETS.coverBackground,
        elements: [
          coverLogo(),
          textEl({
            x: 48,
            y: 320,
            w: 920,
            h: 190,
            text: title,
            font: 'display',
            fontSize: 71,
            fontWeight: 700,
            color: BRAND.white,
          }),
          textEl({
            x: 48,
            y: 540,
            w: 900,
            h: 90,
            text: subtitle || 'Presenter name\nDate',
            font: 'label',
            fontSize: 30,
            color: BRAND.white,
          }),
        ],
      };

    case 'closing':
      return {
        id: uid('slide_'),
        layoutId,
        background: BRAND.blue,
        backgroundImage: ASSETS.coverBackground,
        elements: [
          coverLogo(),
          textEl({
            x: 47,
            y: 265,
            w: 832,
            h: 112,
            text: title,
            font: 'display',
            fontSize: 71,
            fontWeight: 700,
            color: BRAND.white,
          }),
          textEl({
            x: 47,
            y: 390,
            w: 832,
            h: 60,
            text: subtitle || 'Questions and discussion',
            font: 'label',
            fontSize: 28,
            color: BRAND.white,
          }),
        ],
      };

    case 'exec_summary':
      return contentSlide(layoutId, slideIndex, [
        header(title),
        textEl({
          x: CONTENT.x,
          y: CONTENT.top,
          w: CONTENT.w,
          h: CONTENT.bottom - CONTENT.top,
          text: bulletText(bullets),
          fontSize: 28,
        }),
      ]);

    case 'kpi_3_card': {
      const kpis = c.kpis?.length
        ? c.kpis
        : [
            { metric: '98.2%', label: 'Uptime', delta: '+0.4pp' },
            { metric: '14d', label: 'Lead time', delta: '−3d' },
            { metric: '42', label: 'Shipped', delta: '+8' },
          ];
      const gap = 35;
      const cardW = (CONTENT.w - gap * 2) / 3;
      const cards: SlideElement[] = kpis.slice(0, 3).map((k, i) => ({
        id: uid('kpi_'),
        type: 'kpi_card' as const,
        x: CONTENT.x + i * (cardW + gap),
        y: 170,
        w: cardW,
        h: 300,
        metric: k.metric,
        label: k.label,
        delta: k.delta || '',
        fill: BRAND.surface,
        radius: 8,
      }));
      return contentSlide(layoutId, slideIndex, [header(title), ...cards]);
    }

    case 'two_column_split': {
      const colGap = 51;
      const colW = (CONTENT.w - colGap) / 2;
      const rightX = CONTENT.x + colW + colGap;
      return contentSlide(layoutId, slideIndex, [
        header(title),
        textEl({
          x: CONTENT.x,
          y: CONTENT.top + 12,
          w: colW,
          h: 40,
          text: c.leftTitle || 'Progress',
          fontSize: 28,
          fontWeight: 700,
          color: BRAND.blue,
        }),
        textEl({
          x: CONTENT.x,
          y: CONTENT.top + 68,
          w: colW,
          h: 460,
          text: bulletText(c.leftBullets || bullets),
        }),
        textEl({
          x: rightX,
          y: CONTENT.top + 12,
          w: colW,
          h: 40,
          text: c.rightTitle || 'Risks & asks',
          fontSize: 28,
          fontWeight: 700,
          color: BRAND.blue,
        }),
        textEl({
          x: rightX,
          y: CONTENT.top + 68,
          w: colW,
          h: 460,
          text: bulletText(c.rightBullets || ['Risk A', 'Ask for leadership']),
        }),
      ]);
    }

    case 'chart_and_takeaways':
      return contentSlide(layoutId, slideIndex, [
        header(title),
        {
          id: uid('chart_'),
          type: 'chart',
          x: CONTENT.x,
          y: CONTENT.top + 12,
          w: 640,
          h: 520,
          chartType: c.chartType || 'bar',
          chartLabels: c.chartLabels || ['W1', 'W2', 'W3', 'W4'],
          chartValues: c.chartValues || [12, 18, 15, 22],
          fill: BRAND.surface,
          radius: 8,
        },
        textEl({
          x: 724,
          y: CONTENT.top + 12,
          w: 403,
          h: 40,
          text: 'Takeaways',
          fontSize: 28,
          fontWeight: 700,
          color: BRAND.blue,
        }),
        textEl({
          x: 724,
          y: CONTENT.top + 68,
          w: 403,
          h: 460,
          text: bulletText(c.takeaways || bullets),
        }),
      ]);

    case 'timeline_roadmap': {
      const milestones = c.milestones?.length
        ? c.milestones
        : [
            { date: 'Q1', title: 'Foundation', detail: 'Stabilize platform' },
            { date: 'Q2', title: 'Scale', detail: 'Roll out to all teams' },
            { date: 'Q3', title: 'Optimize', detail: 'Cost & reliability' },
            { date: 'Q4', title: 'Expand', detail: 'New markets' },
          ];
      return contentSlide(layoutId, slideIndex, [
        header(title),
        {
          id: uid('tl_'),
          type: 'timeline',
          x: CONTENT.x,
          y: 180,
          w: CONTENT.w,
          h: 360,
          milestones,
        },
      ]);
    }

    case 'section_divider':
      return {
        id: uid('slide_'),
        layoutId,
        background: BRAND.blue,
        backgroundImage: ASSETS.coverBackground,
        elements: [
          coverLogo(),
          textEl({
            x: 48,
            y: 280,
            w: 920,
            h: 120,
            text: title,
            font: 'display',
            fontSize: 56,
            fontWeight: 700,
            color: BRAND.white,
          }),
          textEl({
            x: 48,
            y: 420,
            w: 720,
            h: 60,
            text: subtitle || 'Section overview',
            font: 'label',
            fontSize: 26,
            color: BRAND.white,
          }),
        ],
      };

    case 'agenda': {
      const items = bullets.slice(0, 6);
      return contentSlide(layoutId, slideIndex, [
        header(title || 'Agenda'),
        textEl({
          x: CONTENT.x,
          y: CONTENT.top + 8,
          w: CONTENT.w,
          h: CONTENT.bottom - CONTENT.top - 8,
          text: items.map((b, i) => `${i + 1}.  ${b}`).join('\n\n'),
          fontSize: 30,
          fontWeight: 500,
        }),
      ]);
    }

    case 'three_column': {
      const gap = 28;
      const colW = (CONTENT.w - gap * 2) / 3;
      const cols = [
        {
          x: CONTENT.x,
          title: c.leftTitle || 'Focus',
          bullets: c.leftBullets || bullets.slice(0, 3),
        },
        {
          x: CONTENT.x + colW + gap,
          title: c.midTitle || 'Progress',
          bullets: c.midBullets || ['Update one', 'Update two'],
        },
        {
          x: CONTENT.x + (colW + gap) * 2,
          title: c.rightTitle || 'Next',
          bullets: c.rightBullets || ['Action one', 'Action two'],
        },
      ];
      const els: SlideElement[] = [header(title)];
      for (const col of cols) {
        els.push(
          textEl({
            x: col.x,
            y: CONTENT.top + 12,
            w: colW,
            h: 40,
            text: col.title,
            fontSize: 26,
            fontWeight: 700,
            color: BRAND.blue,
          }),
          textEl({
            x: col.x,
            y: CONTENT.top + 68,
            w: colW,
            h: 460,
            text: bulletText(col.bullets),
            fontSize: 22,
          }),
        );
      }
      return contentSlide(layoutId, slideIndex, els);
    }

    case 'quote_highlight':
      return contentSlide(layoutId, slideIndex, [
        header(title || 'Leadership note'),
        {
          id: uid('qb_'),
          type: 'quote_block',
          x: CONTENT.x,
          y: 160,
          w: CONTENT.w,
          h: 420,
          text: c.quote || c.subtitle || '“Clarity and ownership beat complexity every time.”',
          attribution: c.attribution || '— Leadership team',
          fill: BRAND.surface,
          radius: 8,
        },
      ]);

    case 'image_text': {
      const imgW = 480;
      const textX = CONTENT.x + imgW + 40;
      const textW = CONTENT.w - imgW - 40;
      return contentSlide(layoutId, slideIndex, [
        header(title),
        {
          id: uid('img_'),
          type: 'image',
          name: 'Feature image',
          x: CONTENT.x,
          y: CONTENT.top + 8,
          w: imgW,
          h: 480,
          src: ASSETS.sideStrip,
          fit: 'cover',
          radius: 8,
        },
        textEl({
          x: textX,
          y: CONTENT.top + 12,
          w: textW,
          h: 40,
          text: c.leftTitle || 'Highlights',
          fontSize: 28,
          fontWeight: 700,
          color: BRAND.blue,
        }),
        textEl({
          x: textX,
          y: CONTENT.top + 68,
          w: textW,
          h: 420,
          text: bulletText(c.leftBullets || bullets),
        }),
      ]);
    }

    case 'blank_custom':
      return contentSlide(layoutId, slideIndex, [
        header(title || 'Custom slide'),
        textEl({
          x: CONTENT.x,
          y: CONTENT.top + 24,
          w: CONTENT.w * 0.7,
          h: 80,
          text: 'Drop text, blocks, media, or charts here.\nPin this layout as a favorite for your preferred starting point.',
          fontSize: 22,
          color: BRAND.gray,
        }),
      ]);
  }
}

function defaultTitle(layoutId: MasterLayoutId): string {
  const map: Record<MasterLayoutId, string> = {
    title_slide: 'Weekly Leadership Update',
    exec_summary: 'Executive Summary',
    kpi_3_card: 'Key Performance Indicators',
    two_column_split: 'Progress & Risks',
    three_column: 'Priorities at a Glance',
    chart_and_takeaways: 'Performance Trend',
    timeline_roadmap: 'Delivery Roadmap',
    agenda: 'Agenda',
    section_divider: 'Next section',
    quote_highlight: 'Leadership note',
    image_text: 'In focus',
    blank_custom: 'Custom slide',
    closing: 'Thank you',
  };
  return map[layoutId];
}

/** Preserve editable text when switching layouts. */
export function switchLayout(slide: Slide, nextLayout: MasterLayoutId, index: number): Slide {
  const next = buildLayout(nextLayout, index, slideToPayload(slide));
  next.id = slide.id;
  next.notes = slide.notes;
  return next;
}

/** Pull editable content from a slide so AI / layout switches can rebuild it. */
export function slideToPayload(slide: Slide): Partial<MagicSlidePayload> {
  const texts = slide.elements
    .filter((e) => e.type === 'text' && e.text && !e.locked)
    .map((e) => e.text!)
    .filter(Boolean);

  const payload: Partial<MagicSlidePayload> = {
    layoutId: slide.layoutId,
    title: texts[0],
    subtitle: texts[1],
    bullets: texts.slice(1).flatMap((t) =>
      t
        .split('\n')
        .map((l) => l.replace(/^[•\-]\s*/, '').trim())
        .filter(Boolean),
    ),
  };

  const kpi = slide.elements.filter((e) => e.type === 'kpi_card');
  if (kpi.length) {
    payload.kpis = kpi.map((k) => ({
      metric: k.metric || '',
      label: k.label || '',
      delta: k.delta,
    }));
  }

  const chart = slide.elements.find((e) => e.type === 'chart');
  if (chart) {
    payload.chartType = chart.chartType;
    payload.chartLabels = chart.chartLabels;
    payload.chartValues = chart.chartValues;
  }

  const tl = slide.elements.find((e) => e.type === 'timeline');
  if (tl?.milestones) payload.milestones = tl.milestones;

  const quote = slide.elements.find((e) => e.type === 'quote_block');
  if (quote) {
    payload.quote = quote.text;
    payload.attribution = quote.attribution;
  }

  return payload;
}

/** Rebuild a slide in-place from an AI / editor payload, keeping id + notes. */
export function applyPayloadToSlide(
  slide: Slide,
  index: number,
  payload: Partial<MagicSlidePayload>,
): Slide {
  const next = buildLayout(slide.layoutId, index, { ...slideToPayload(slide), ...payload, layoutId: slide.layoutId });
  next.id = slide.id;
  next.notes = slide.notes;
  return next;
}

export function hydrateMagicDeck(
  title: string,
  payloads: MagicSlidePayload[],
): { title: string; slides: Slide[] } {
  const slides = payloads.map((p, i) => buildLayout(p.layoutId, i, p));
  return { title, slides };
}

export function createStarterDeck(): Slide[] {
  return [
    buildLayout('title_slide', 0, {
      title: 'Weekly Leadership Update',
      subtitle: 'IT Delivery · Ops · Security',
    }),
    buildLayout('exec_summary', 1),
    buildLayout('kpi_3_card', 2),
    buildLayout('closing', 3),
  ];
}

/** Smart block factories for drag-drop */
export function createSmartBlock(kind: SmartBlockKind, at: { x: number; y: number }): SlideElement {
  switch (kind) {
    case 'kpi_card':
      return {
        id: uid('kpi_'),
        type: 'kpi_card',
        x: at.x,
        y: at.y,
        w: 280,
        h: 160,
        metric: '42%',
        label: 'Metric',
        delta: '+5%',
        fill: BRAND.surface,
        radius: 8,
      };
    case 'pros_cons':
      return {
        id: uid('pc_'),
        type: 'pros_cons',
        x: at.x,
        y: at.y,
        w: 520,
        h: 240,
        pros: ['Faster delivery', 'Clear ownership'],
        cons: ['Migration risk', 'Capacity squeeze'],
        fill: BRAND.surface,
        radius: 8,
      };
    case 'callout':
      return {
        id: uid('co_'),
        type: 'callout',
        x: at.x,
        y: at.y,
        w: 640,
        h: 88,
        text: 'Decision needed: approve Q3 capacity plan by Friday.',
        calloutTone: 'info',
        fill: BRAND.surface,
        radius: 8,
      };
    case 'process_row':
      return {
        id: uid('pr_'),
        type: 'process_row',
        x: at.x,
        y: at.y,
        w: 1060,
        h: 140,
        steps: [
          { title: 'Discover', body: 'Scope & risks' },
          { title: 'Build', body: 'Ship increments' },
          { title: 'Validate', body: 'UAT & metrics' },
          { title: 'Scale', body: 'Rollout' },
        ],
      };
    case 'quote_block':
      return {
        id: uid('qb_'),
        type: 'quote_block',
        x: at.x,
        y: at.y,
        w: 640,
        h: 200,
        text: '“Ship the decision, then refine the details.”',
        attribution: '— Program lead',
        fill: BRAND.surface,
        radius: 8,
      };
    case 'checklist':
      return {
        id: uid('ck_'),
        type: 'checklist',
        x: at.x,
        y: at.y,
        w: 420,
        h: 240,
        items: ['Confirm owners', 'Lock timeline', 'Brief stakeholders', 'Open risk log'],
        fill: BRAND.surface,
        radius: 8,
      };
    case 'stat_row':
      return {
        id: uid('sr_'),
        type: 'stat_row',
        x: at.x,
        y: at.y,
        w: 960,
        h: 120,
        stats: [
          { metric: '12', label: 'Active' },
          { metric: '4', label: 'At risk' },
          { metric: '98%', label: 'SLA' },
          { metric: '3d', label: 'Cycle' },
        ],
        fill: BRAND.surface,
        radius: 8,
      };
    case 'team_card':
      return {
        id: uid('tc_'),
        type: 'team_card',
        x: at.x,
        y: at.y,
        w: 320,
        h: 180,
        text: 'Alex Rivera',
        role: 'Delivery lead',
        contact: 'alex@astrix.example',
        fill: BRAND.surface,
        radius: 8,
      };
  }
}

export function renumberSlides(slides: Slide[]): Slide[] {
  return slides.map((slide, i) => ({
    ...slide,
    elements: slide.elements.map((el) =>
      el.type === 'slide_number' ? { ...el, text: String(i + 1) } : el,
    ),
  }));
}
